/**
 * Authentication Context Provider
 * Manages user authentication state using Supabase Auth
 * Provides user session, loading state, and sign out functionality
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@xanectech/supabase-client';
import { AuthService } from '../services/AuthService';
import { secureSession } from '../lib/secure-session';
import { logger, useEnvironment } from '@xanectech/lib';
import { OptimizedAuthProvider } from './OptimizedAuthContext';
import { clearSession } from '@xanectech/encryption';
import { clearPasswordCache } from '../lib/securePasswordManager';
import { sessionTimeoutManager } from '../lib/sessionTimeoutManager';
import {
  initializeStorageLogger,
  cleanupStorageLogger,
} from '../lib/storageLogger';
import { performLogoutCleanup } from '../lib/storageCleanup';

const PORTAL_OUTCOME_MANAGED_SLUGS = new Set(['xtech-admin', 'xtech-company']);

/**
 * Type definition for the authentication context
 */
export interface AuthContextType {
  user: User | null; // Current authenticated user
  session: Session | null; // Current auth session
  loading: boolean; // Loading state for auth operations
  encryptionReady: boolean; // Whether encryption keys are ready
  signOut: () => Promise<void>; // Function to sign out user
  portal: string; // Application portal identifier
}

// Create authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Custom hook to access authentication context
 * Must be used within an AuthProvider component
 *
 * @returns AuthContextType - Authentication state and methods
 * @throws Error if used outside of AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Props for the AuthProvider component
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider Component
 * Manages authentication state and provides it to child components
 * Handles session management, user state, and authentication events
 *
 * @param children - React components to wrap with auth context
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Authentication state
  const [user, setUser] = useState<User | null>(null);
  const { appVars } = useEnvironment();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [encryptionReady, setEncryptionReady] = useState(false);

  /**
   * Signs out the current user using AuthService
   */
  const signOut = useCallback(async (): Promise<void> => {
    try {
      const currentUserId = user?.id;
      const clearAllIndexedDBDatabases = async (): Promise<void> => {
        try {
          const databases = await indexedDB.databases();
          logger.info('Clearing IndexedDB databases on logout', {
            databaseCount: databases.length,
          });

          for (const db of databases) {
            if (!db.name) {
              continue;
            }

            const deleted = await new Promise<boolean>((resolve) => {
              const deleteRequest = indexedDB.deleteDatabase(db.name as string);
              const timeout = window.setTimeout(() => resolve(false), 1500);

              deleteRequest.onsuccess = () => {
                window.clearTimeout(timeout);
                resolve(true);
              };

              deleteRequest.onerror = () => {
                window.clearTimeout(timeout);
                resolve(false);
              };

              deleteRequest.onblocked = () => {
                window.clearTimeout(timeout);
                logger.warn('IndexedDB deletion blocked during logout', {
                  database: db.name,
                });
                resolve(false);
              };
            });

            if (!deleted) {
              logger.warn('Failed to delete IndexedDB during logout', {
                database: db.name,
              });
            }
          }

          logger.info('IndexedDB cleanup sweep completed');
        } catch (error) {
          logger.warn('Failed to clear IndexedDB databases', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      };

      // Stop session timeout
      sessionTimeoutManager.stop();

      // Clean up storage logger
      await cleanupStorageLogger();

      // SECURITY: Clear all encryption-related data
      clearSession();
      clearPasswordCache();
      setEncryptionReady(false);

      await clearAllIndexedDBDatabases();

      // Force clear local state first
      setUser(null);
      setSession(null);

      const logoutDetails = {
        email: user?.email ?? null,
        logout_reason: 'user_initiated',
        occurred_at: new Date().toISOString(),
        portal: String(appVars?.APP_SLUG ?? 'trueid-locker'),
        requested_path: window.location.pathname,
        user_agent: navigator.userAgent,
        user_id: currentUserId ?? null,
      };

      // Use AuthService to handle sign out
      const { error } = await AuthService.signOut(logoutDetails);

      if (error) {
        logger.error(
          'Sign out error',
          error instanceof Error ? error : { error: String(error) },
          'auth',
        );
      }

      // SECURITY: Comprehensive storage cleanup
      await performLogoutCleanup({
        clearAllLocalStorage: true,
        clearEncryptionKeys: true,
        userId: currentUserId,
      });

      // Final IndexedDB sweep after auth sign-out and state reset
      await clearAllIndexedDBDatabases();

      logger.info('User signed out successfully', {}, 'auth');
    } catch (error) {
      logger.error(
        'Sign out error',
        error instanceof Error ? error : { error: String(error) },
        'auth',
      );
    }
    // eslint-disable-next-line react_hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;

    // Initialize authentication state

    // Initialize secure session management (online mode only)
    secureSession.initialize({
      sessionTimeout: 24 * 60 * 60 * 1000, // 24 hours
      maxInactivityTime: 2 * 60 * 60 * 1000, // 2 hours
      enableAutoLogout: true,
    });

    // Set up real-time auth state listener using AuthService FIRST
    const {
      data: { subscription },
    } = AuthService.onAuthStateChange((event, session) => {
      if (!mounted) return;

      // Only synchronous state updates here to prevent deadlocks
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Defer all async operations to prevent blocking auth state change
      if (event === 'SIGNED_IN' && session?.user) {
        setTimeout(async () => {
          if (!mounted) return;
          try {
            const appSlug = String(appVars?.APP_SLUG ?? 'trueid-locker');
            const isOutcomeManagedPortal =
              PORTAL_OUTCOME_MANAGED_SLUGS.has(appSlug);

            if (!isOutcomeManagedPortal) {
              await secureSession.logSecurityEvent('user_login');
            }

            // Start session timeout timer
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('auth_session_duration')
              .eq('user_id', session.user.id)
              .single();

            const sessionDuration = profile?.auth_session_duration || 480; // Default 8 hours
            if (sessionDuration > 0) {
              sessionTimeoutManager.start({
                durationMinutes: sessionDuration,
                onTimeout: async () => {
                  logger.info('Session timeout - auto logout', {}, 'auth');
                  await signOut();
                },
                warningMinutes: 5, // Warn 5 minutes before timeout
                onWarning: () => {
                  // Could show a toast warning here
                  logger.info('Session expiring soon', {}, 'auth');
                },
              });
            }

            // Initialize storage logger for this user
            await initializeStorageLogger();
            logger.info('Storage logger initialized', {}, 'auth');

            // SECURITY: Let EncryptionProvider handle encryption setup/unlock
            // Do not auto-initialize here to avoid password conflicts
            const currentPath = window.location.pathname;
            const isUserApp = currentPath.startsWith('/app');

            if (isUserApp) {
            }
          } catch (err) {
            logger.error(
              'Failed to process sign-in side effects',
              err instanceof Error ? err : { error: String(err) },
              'auth',
            );
          }
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        // Stop session timeout on logout
        sessionTimeoutManager.stop();
        setTimeout(async () => {
          if (!mounted) return;
          try {
            await secureSession.logSecurityEvent('user_logout', {
              occurred_at: new Date().toISOString(),
              portal: String(appVars?.APP_SLUG ?? 'trueid-locker'),
              requested_path: window.location.pathname,
              user_agent: navigator.userAgent,
            });
          } catch (err) {
            logger.error(
              'Error during sign out logging',
              err instanceof Error ? err : { error: String(err) },
              'auth',
            );
          }
        }, 0);
      }
    });

    // THEN get initial session using AuthService
    void AuthService.getCurrentSession().then(({ session, error }) => {
      if (!mounted) return;

      if (error) {
        logger.error('Failed to get initial session', error, 'auth');
      }

      logger.info(
        'Initial session check completed',
        {
          hasSession: !!session,
          userId: session?.user?.id,
          email: session?.user?.email,
        },
        'auth',
      );

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Note: Encryption setup and unlock handled by EncryptionProvider
      // Check for valid session to enable manual unlock flow
      if (session?.user?.id) {
        setTimeout(async () => {
          try {
            const currentPath = window.location.pathname;
            const isUserApp = currentPath.startsWith('/app');

            if (!isUserApp) return; // Only relevant for user app
          } catch (error) {
            logger.error(
              'Failed to check encryption status',
              error instanceof Error ? error : { error: String(error) },
              'auth',
            );
          }
        }, 0);
      }
    });

    // Cleanup subscription on unmount
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [appVars, signOut]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      user,
      session,
      loading,
      encryptionReady,
      signOut,
      portal: appVars?.APP_SLUG ?? 'trueid-locker',
    }),
    [user, session, loading, encryptionReady, signOut, appVars],
  );

  return (
    <OptimizedAuthProvider
      user={user}
      loading={loading}
      signOut={signOut}
      refreshSession={async () => {
        const { session } = await AuthService.getCurrentSession();
        setSession(session);
        setUser(session?.user ?? null);
      }}
      updateProfile={async (updates: unknown) => {
        // Implement profile update logic
        const metadata =
          updates && typeof updates === 'object'
            ? Object.entries(updates as Record<string, unknown>).reduce(
                (acc, [key, value]) => {
                  acc[key] =
                    typeof value === 'string' ||
                    typeof value === 'number' ||
                    typeof value === 'boolean'
                      ? value
                      : String(value);
                  return acc;
                },
                {} as Record<string, string | number | boolean>,
              )
            : { updates: String(updates) };
      }}
    >
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    </OptimizedAuthProvider>
  );
};
